import { useEffect, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ProFeatureGate } from "@/components/ProFeatureGate";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, HeartPulse, Sparkles, Download, Info, Copy } from "lucide-react";

export default function NhsSupportingInfo() {
  const [isPro, setIsPro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cvs, setCvs] = useState([]);
  const [cvId, setCvId] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [trust, setTrust] = useState("");
  const [spec, setSpec] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    const h = await api.get("/nhs/supporting-info").catch(() => ({ data: [] }));
    setHistory(Array.isArray(h.data) ? h.data : []);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/billing/subscription");
        const pro = data.status === "active" && ["pro", "sponsored"].includes(data.tier);
        setIsPro(pro);
        if (pro) {
          const c = await api.get("/tailored-cvs").catch(() => ({ data: [] }));
          setCvs(Array.isArray(c.data) ? c.data : []);
          await loadHistory();
        }
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async () => {
    if (!spec.trim()) { toast.error("Paste the NHS person specification / job description"); return; }
    if (!cvId) { toast.error("Select a tailored CV to base the statement on"); return; }
    setGenerating(true);
    setResult(null);
    try {
      const { data: started } = await api.post("/nhs/supporting-info/generate", {
        tailored_cv_id: cvId, person_spec: spec, job_title: jobTitle, trust,
      });
      const id = started.id;
      for (let i = 0; i < 80; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data } = await api.get(`/nhs/supporting-info/${id}`);
        if (data.status === "done") { setResult(data); toast.success("Supporting statement ready"); break; }
        if (data.status === "error") { toast.error("Generation failed. Please try again."); break; }
      }
      await loadHistory();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Generation failed");
    } finally { setGenerating(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  if (!isPro) return (
    <div className="py-10" data-testid="nhs-page">
      <div className="text-center mb-8">
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight flex items-center justify-center gap-2"><HeartPulse className="h-7 w-7 text-[#005EB8]" /> NHS Supporting Information</h1>
      </div>
      <ProFeatureGate
        description="Generate a ~1,400-word NHS 'supporting information' statement mapped to the person specification — grounded entirely in your real experience."
        features={["Addresses every essential & desirable criterion", "Evidenced from your tailored CV — no fabrication", "Reflects NHS values", "Download-ready PDF to paste into TRAC / jobs.nhs.uk"]}
      />
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="nhs-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-2"><HeartPulse className="h-7 w-7 text-[#005EB8]" /> NHS Supporting Information</h1>
        <p className="text-muted-foreground mt-1 text-sm">Paste the NHS person specification, pick a tailored CV, and get a substantial supporting statement built only from your real experience.</p>
      </div>

      <Card className="p-6 border-zinc-200" data-testid="nhs-form-card">
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider font-semibold">Job title</Label><Input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="e.g. Band 5 Staff Nurse" data-testid="nhs-job-title" /></div>
            <div><Label className="text-xs uppercase tracking-wider font-semibold">NHS trust / organisation</Label><Input value={trust} onChange={(e) => setTrust(e.target.value)} placeholder="e.g. Guy's and St Thomas' NHS FT" data-testid="nhs-trust" /></div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Tailored CV (source of truth)</Label>
            <Select value={cvId} onValueChange={setCvId}>
              <SelectTrigger data-testid="nhs-cv-select"><SelectValue placeholder={cvs.length ? "Select a tailored CV" : "No tailored CVs yet — tailor one first"} /></SelectTrigger>
              <SelectContent>{cvs.map((c) => <SelectItem key={c.id} value={c.id}>{c.job_title || "Untitled"}{c.company ? ` — ${c.company}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Person specification / job description</Label>
            <Textarea rows={8} value={spec} onChange={(e) => setSpec(e.target.value)} placeholder="Paste the essential and desirable criteria from the NHS advert here…" data-testid="nhs-spec" />
          </div>
          <p className="flex items-start gap-1.5 text-xs bg-[#005EB8]/5 border border-[#005EB8]/20 rounded-md p-2 text-[#005EB8]" data-testid="nhs-cv-tip"><Info className="h-4 w-4 shrink-0 mt-0.5" /> For the strongest result, pick a CV suited to this role. We first enhance it to align closely (90–100%) with the person specification, then write your 1,400–1,500 word statement.</p>
          <p className="flex items-start gap-1.5 text-xs bg-blue-50 border border-blue-200 rounded-md p-2 text-blue-800"><Info className="h-4 w-4 shrink-0 mt-0.5" /> We never invent experience or registrations. If the spec asks for something not in your CV, the statement honestly frames transferable skills instead. Always review before submitting.</p>
          <Button onClick={generate} disabled={generating} className="active:scale-[0.98] transition-transform" data-testid="nhs-generate-btn">
            {generating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}{generating ? "Writing your statement…" : "Generate supporting information"}
          </Button>
        </div>
      </Card>

      {result && result.status === "done" && (
        <Card className="p-6 border-zinc-200" data-testid="nhs-result-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-bold">Your supporting statement</h2>
            <Button variant="outline" size="sm" onClick={async () => { try { await navigator.clipboard.writeText(result.supporting_info || ""); toast.success("Copied — paste it straight into TRAC"); } catch { toast.error("Couldn't copy."); } }} data-testid="nhs-copy">
              <Copy className="h-4 w-4 mr-1.5" /> Copy
            </Button>
            <a href={`${API}/nhs/supporting-info/${result.id}/download?fmt=docx`} target="_blank" rel="noreferrer" data-testid="nhs-download-word">
              <Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1.5" /> Download Word</Button>
            </a>
            <a href={`${API}/nhs/supporting-info/${result.id}/download?fmt=pdf`} target="_blank" rel="noreferrer" data-testid="nhs-download">
              <Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1.5" /> Download PDF</Button>
            </a>
          </div>
          <p className="text-xs text-muted-foreground mb-3" data-testid="nhs-word-count">Approx. {result.word_count} words</p>
          <div className="text-sm whitespace-pre-wrap leading-relaxed bg-zinc-50 border rounded-lg p-4" data-testid="nhs-statement-text">{result.supporting_info}</div>
        </Card>
      )}

      {history.length > 0 && (
        <Card className="p-5 border-zinc-200" data-testid="nhs-history-card">
          <h2 className="font-heading text-base font-bold mb-3">Recent statements</h2>
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} className="flex items-center justify-between text-sm border-b border-zinc-100 pb-2 last:border-0" data-testid={`nhs-history-${h.id}`}>
                <span className="truncate">{h.job_title || "Untitled"}{h.trust ? ` — ${h.trust}` : ""} <span className="text-muted-foreground text-xs">({h.status})</span></span>
                {h.status === "done" && <a href={`${API}/nhs/supporting-info/${h.id}/download?fmt=docx`} target="_blank" rel="noreferrer" className="text-[#005EB8] text-xs font-medium shrink-0 ml-2">Word</a>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
